import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { requestService, Request, RequestPayloadCreateUser, RequestPayloadUpdateRole, RequestPayloadCreateProject } from '@/services/requestService'
import { analyzeError, ErrorInfo } from '@/lib/errorUtils'
import { HttpError } from '@/lib/httpError'
import { useAuth } from '@/hooks/useAuth'
import ApproveRequestModal, { ApproveRequestModalRef } from '@/components/modals/ApproveRequestModal'
import RejectRequestModal, { RejectRequestModalRef } from '@/components/modals/RejectRequestModal'
import RequestDetailsModal, { RequestDetailsModalRef } from '@/components/modals/RequestDetailsModal'
import DeclineLicenseRequestModal, { type DeclineLicenseRequestModalRef } from '@/components/modals/DeclineLicenseRequestModal'
import ViewRequestLicenseModal, { type ViewRequestLicenseModalRef } from '@/components/modals/ViewRequestLicenseModal'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { parseLicenseRequestPayload } from '@/services/licenseApiService'

type TabType = 'pending' | 'approved' | 'rejected' | 'my'

export default function UserRequestsPage() {
  const { t } = useTranslation('requests')
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabType>(searchParams.get('tab') === 'my' ? 'my' : 'pending')
  const declineModalRef = useRef<DeclineLicenseRequestModalRef>(null)
  const viewLicenseModalRef = useRef<ViewRequestLicenseModalRef>(null)
  const approveModalRef = useRef<ApproveRequestModalRef>(null)
  const rejectModalRef = useRef<RejectRequestModalRef>(null)
  const detailsModalRef = useRef<RequestDetailsModalRef>(null)

  // Requests data
  const [requests, setRequests] = useState<Request[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ErrorInfo | null>(null)
  const [offset, setOffset] = useState(0)
  const limit = 20

  // Fetch requests based on active tab
  const fetchRequests = async () => {
    setLoading(true)
    setError(null)

    try {
      let response
      if (activeTab === 'pending') {
        response = await requestService.getPendingRequests(offset, limit)
      } else if (activeTab === 'approved') {
        response = await requestService.getApprovedRequests(offset, limit)
      } else if (activeTab === 'rejected') {
        response = await requestService.getRejectedRequests(offset, limit)
      } else {
        response = await requestService.getMyRequests(offset, limit)
      }

      setRequests(response.requests || [])
    } catch (err) {
      // Handle 401 error: try to refresh token
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry fetching requests after successful refresh
          return fetchRequests()
        } catch (refreshError) {
          const errorInfo = analyzeError(refreshError)
          setError(errorInfo)
          setLoading(false)
          return
        }
      }

      // For other errors
      const errorInfo = analyzeError(err)
      setError(errorInfo)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRequests()
  }, [activeTab, offset])

  // Reset offset when changing tabs
  useEffect(() => {
    setOffset(0)
  }, [activeTab])

  const getPayloadSummary = (type: string, payloadStr: string) => {
    try {
      const payload = JSON.parse(payloadStr)
      if (type === 'create_user') {
        const p = payload as RequestPayloadCreateUser
        return `${p.account} (${p.name}) - ${t(`roles.${p.role}`)}`
      } else if (type === 'update_platform_role') {
        const p = payload as RequestPayloadUpdateRole
        return `${t(`roles.${p.role}`)}`
      } else if (type === 'create_project') {
        const p = payload as RequestPayloadCreateProject
        return `${p.name} - ${p.description}`
      } else if (type === 'issue_license') {
        return licenseRequestSummary(payloadStr)
      }
      return payloadStr
    } catch {
      return payloadStr
    }
  }

  // A license request (BAFTT-12): what was asked for, and for whom.
  const licenseRequestSummary = (payloadStr: string) => {
    const p = parseLicenseRequestPayload(payloadStr)
    if (!p) return payloadStr
    const parts = [
      t(`licenseRequests.kind.${p.kind}`, p.kind),
      t(`licenseRequests.type.${p.license_type}`, p.license_type),
      t('licenseRequests.days', { n: p.valid_days, defaultValue: '{{n}} days' }),
    ]
    if (p.max_flows) parts.push(t('licenseRequests.flows', { n: p.max_flows, defaultValue: '{{n}} flows' }))
    return `${parts.join(' · ')} - ${p.customer_name} <${p.email}>`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  const isOwner = user?.platform_role === 'owner'
  const canApproveReject = isOwner && activeTab === 'pending'
  // My Requests shows the answer too, so a customer sees which license was
  // issued for them or why it was declined.
  const showApproverInfo = activeTab === 'approved' || activeTab === 'rejected' || activeTab === 'my'

  const renderRequestsTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-600">{t('loading')}</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{t('error.title')}</p>
          <p className="text-red-600 text-sm mt-1">{error.message}</p>
        </div>
      )
    }

    if (requests.length === 0) {
      return (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('empty.description')}</p>
        </div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t('table.type')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t('table.requestor')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t('table.createdAt')}
              </th>
              {showApproverInfo && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('table.approver')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('table.processedAt')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('table.reason')}
                  </th>
                </>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t('table.details')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t('table.status')}
              </th>
              {canApproveReject && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {t('table.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {t(`requestTypes.${request.type}`)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{request.requestor.name || '-'}</div>
                  <div className="text-sm text-gray-500">{request.requestor.account || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(request.created_at)}
                </td>
                {showApproverInfo && (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{request.approver?.name || '-'}</div>
                      <div className="text-sm text-gray-500">{request.approver?.account || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.processed_at ? formatDate(request.processed_at) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {request.reason ? (
                        <button
                          onClick={() => detailsModalRef.current?.open(request)}
                          className="text-sm text-gray-900 hover:text-primary cursor-pointer max-w-xs truncate block text-left"
                          title={t('table.clickToViewDetails')}
                        >
                          {request.reason}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                  </>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => detailsModalRef.current?.open(request)}
                    className="text-sm text-primary hover:text-primary-dark cursor-pointer max-w-xs truncate block text-left"
                    title={t('table.clickToViewDetails')}
                  >
                    {getPayloadSummary(request.type, request.payload)}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {t(`status.${request.status}`)}
                  </span>
                  {request.type === 'issue_license' && request.has_license && (
                    <button
                      onClick={() => viewLicenseModalRef.current?.open(request.id)}
                      className="ml-2 text-xs font-medium text-primary hover:underline"
                    >
                      {t('licenseRequests.view.action', 'View license')}
                    </button>
                  )}
                </td>
                {canApproveReject && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {request.requestor_id !== user?.id && request.type === 'issue_license' ? (
                      // Approving a license request means signing a key, so it
                      // is answered on the issuing form, behind its own gates.
                      // The server lists these for vendor accounts only.
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/plans/issue?request=${encodeURIComponent(request.id)}`)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium"
                        >
                          {t('licenseRequests.issue', 'Issue')}
                        </button>
                        <button
                          onClick={() => declineModalRef.current?.open(request)}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
                        >
                          {t('actions.reject')}
                        </button>
                      </div>
                    ) : request.requestor_id !== user?.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => approveModalRef.current?.open(request)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium"
                        >
                          {t('actions.approve')}
                        </button>
                        <button
                          onClick={() => rejectModalRef.current?.open(request)}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
                        >
                          {t('actions.reject')}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500 italic">{t('actions.selfRequest')}</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
    <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('description')}</p>
        </div>

        {/* Requests Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex items-center px-6" aria-label="Tabs">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`${
                    activeTab === 'pending'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {t('tabs.pending')}
                </button>
                <button
                  onClick={() => setActiveTab('approved')}
                  className={`${
                    activeTab === 'approved'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {t('tabs.approved')}
                </button>
                <button
                  onClick={() => setActiveTab('rejected')}
                  className={`${
                    activeTab === 'rejected'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  {t('tabs.rejected')}
                </button>
              </div>

              {/* Divider */}
              <div className="mx-6 h-6 w-px bg-gray-300"></div>

              {/* My Requests Button */}
              <button
                onClick={() => setActiveTab('my')}
                className={`${
                  activeTab === 'my'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('tabs.my')}
              </button>
            </nav>
          </div>

          <div className="p-6">
            {renderRequestsTable()}
          </div>

          {/* Pagination */}
          {!loading && !error && requests.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.previous')}
              </button>
              <span className="text-sm text-gray-700">
                {t('pagination.showing')} {offset + 1} - {offset + requests.length}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={requests.length < limit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.next')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ApproveRequestModal ref={approveModalRef} onSuccess={fetchRequests} />
      <RejectRequestModal ref={rejectModalRef} onSuccess={fetchRequests} />
      <RequestDetailsModal ref={detailsModalRef} />
      <DeclineLicenseRequestModal ref={declineModalRef} onDeclined={fetchRequests} />
      <ViewRequestLicenseModal ref={viewLicenseModalRef} />
    </>
  )
}
